/**
 * Per-assignment detail viewer at ``/my/documents/:id``.
 *
 * The welcome email (and Pilot's onboarding fan-out) deep-links here:
 * ``${APP_URL}/my/documents/${assignment.id}``. Without this page that
 * URL was falling through to the list page and the new hire couldn't
 * actually open the document they were emailed about.
 *
 * Behavior:
 *   * Server stamps ``viewed_at`` on first GET so the dashboard counter
 *     and Sentinel both pick up the open immediately.
 *   * If ``template.file_url`` is null (empty template) we show a
 *     friendly placeholder + "Contact HR" copy instead of an iframe to
 *     a 404 PDF.
 *   * Acknowledge / Sign buttons mirror the list page's affordances
 *     so the UX is identical regardless of how the employee landed.
 */
import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import {
  AlertCircle,
  ArrowLeft,
  CheckCircle2,
  ExternalLink,
  FileText,
  Loader2,
  PenLine,
} from "lucide-react";
import { pageTransition, formatDate } from "@/lib/utils";
import { Card, CardHeader, CardTitle } from "@/components/ui/Card";
import { Button } from "@/components/ui/Button";
import { Badge } from "@/components/ui/Badge";
import { SkeletonCard } from "@/components/ui/Skeleton";
import { useToast } from "@/hooks/useToast";
import {
  acknowledgeAssignment,
  getMyDocument,
  getMyForm,
  saveMyFormDraft,
  signAssignment,
  submitMyForm,
  type DocumentAssignment,
} from "@/api/documentAssignments";
import { FormRenderer } from "@/components/forms/FormRenderer";
import {
  emptyFormData,
  validateFormData,
  type FormField,
} from "@/components/forms/types";

export function MyDocumentDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { addToast } = useToast();

  const [assignment, setAssignment] = useState<DocumentAssignment | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    if (!id) return;
    setLoadError(null);
    try {
      const data = await getMyDocument(id);
      setAssignment(data);
    } catch (err: unknown) {
      const ax = err as { response?: { status?: number; data?: { detail?: string } } };
      // 404 either means the row doesn't exist OR it's not theirs;
      // either way, route the employee back to the list rather than
      // leaking which case it was.
      if (ax.response?.status === 404) {
        setLoadError("This document isn't available on your account.");
      } else {
        setLoadError(
          ax.response?.data?.detail || "Couldn't load this document.",
        );
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const acknowledge = async () => {
    if (!id) return;
    setBusy(true);
    try {
      await acknowledgeAssignment(id);
      addToast("success", "Acknowledged");
      load();
    } catch {
      addToast("error", "Couldn't acknowledge");
    } finally {
      setBusy(false);
    }
  };

  const sign = async () => {
    if (!id) return;
    setBusy(true);
    try {
      await signAssignment(id);
      addToast("success", "Signed");
      load();
    } catch {
      addToast("error", "Couldn't sign");
    } finally {
      setBusy(false);
    }
  };

  if (loading) {
    return (
      <div className="space-y-4">
        <SkeletonCard />
        <SkeletonCard />
      </div>
    );
  }

  if (loadError || !assignment) {
    return (
      <motion.div {...pageTransition}>
        <Link
          to="/my-documents"
          className="flex items-center gap-1 text-sm text-text-muted hover:text-text-primary mb-4"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to My Documents
        </Link>
        <Card>
          <div className="py-10 text-center">
            <AlertCircle className="h-8 w-8 mx-auto mb-2 text-danger" />
            <p className="text-sm text-text-primary font-medium">
              {loadError ?? "Document not found"}
            </p>
            <Button
              size="sm"
              variant="secondary"
              className="mt-4"
              onClick={() => navigate("/my-documents")}
            >
              Open My Documents
            </Button>
          </div>
        </Card>
      </motion.div>
    );
  }

  const tpl = assignment.template;
  const requiresSig = tpl?.requires_signature ?? false;
  const isDone = assignment.status === "signed" || assignment.status === "waived";
  const isOverdue = assignment.is_overdue || assignment.status === "overdue";
  const fileUrl = tpl?.file_url ?? null;
  const isForm = tpl?.template_type === "form";

  return (
    <motion.div {...pageTransition}>
      <Link
        to="/my-documents"
        className="flex items-center gap-1 text-sm text-text-muted hover:text-text-primary mb-4"
      >
        <ArrowLeft className="h-4 w-4" />
        Back to My Documents
      </Link>

      <div className="mb-6">
        <h2 className="text-2xl font-semibold font-heading text-text-primary flex items-center gap-2 flex-wrap">
          <FileText className="h-6 w-6 text-accent" />
          {tpl?.name ?? "Document"}
        </h2>
        <div className="mt-2 flex items-center gap-2 flex-wrap">
          {assignment.status === "signed" && (
            <Badge variant="success">Signed</Badge>
          )}
          {assignment.status === "waived" && (
            <Badge variant="default">Waived</Badge>
          )}
          {assignment.status === "viewed" && !isDone && (
            <Badge variant="info">Viewed</Badge>
          )}
          {assignment.status === "pending" && !isDone && (
            <Badge variant="warning">Pending</Badge>
          )}
          {isOverdue && <Badge variant="danger">Overdue</Badge>}
          {tpl?.category && <Badge variant="default">{tpl.category}</Badge>}
          {requiresSig && <Badge variant="info">Signature required</Badge>}
        </div>
        {tpl?.description && (
          <p className="text-sm text-text-muted mt-2">{tpl.description}</p>
        )}
        <p className="text-xs text-text-muted mt-1">
          Assigned {formatDate(assignment.assigned_at)}
          {assignment.deadline_at
            ? ` · Due ${formatDate(assignment.deadline_at)}`
            : ""}
          {assignment.completed_at
            ? ` · Completed ${formatDate(assignment.completed_at)}`
            : ""}
        </p>
      </div>

      {/* Digital form — full-page form fill experience. Replaces both
          the PDF viewer and the sign card; the form is the document
          for these assignments. */}
      {isForm && id && (
        <FormFillCard assignmentId={id} onSubmitted={load} isDone={isDone} />
      )}

      {/* Document viewer. PDFs render inline via <iframe>; anything
          else (or a NULL file_url) falls back to a friendly placeholder
          rather than a broken embed. Skipped entirely for form-type
          assignments — those render via FormFillCard above. */}
      {!isForm && (
      <Card className="mb-4">
        <CardHeader>
          <CardTitle>Document</CardTitle>
          {fileUrl && (
            <a
              href={fileUrl}
              target="_blank"
              rel="noreferrer"
              className="text-sm text-accent hover:underline inline-flex items-center gap-1"
            >
              <ExternalLink className="h-4 w-4" />
              Open in new tab
            </a>
          )}
        </CardHeader>
        {fileUrl ? (
          <div className="w-full overflow-hidden rounded-md border border-border/40">
            <iframe
              src={fileUrl}
              title={tpl?.name ?? "Document"}
              className="w-full h-[70vh] bg-white"
            />
          </div>
        ) : (
          <div className="py-10 text-center text-sm">
            <AlertCircle className="h-8 w-8 mx-auto mb-2 text-warning" />
            <p className="text-text-primary font-medium">
              Document not available yet.
            </p>
            <p className="text-text-muted mt-1">
              Your HR admin hasn&rsquo;t uploaded the file for this
              template. Please contact them before signing.
            </p>
          </div>
        )}
      </Card>
      )}

      {/* Action area. Hidden once the row is signed/waived to keep
          the page usable as a permanent record. Skipped for form-type
          assignments — FormFillCard owns its own submit button. */}
      {!isForm && !isDone && (
        <Card>
          <CardHeader>
            <CardTitle>
              {requiresSig ? "Sign this document" : "Acknowledge"}
            </CardTitle>
          </CardHeader>
          <p className="text-sm text-text-muted">
            {requiresSig
              ? "By signing you confirm you've read this document and agree to its terms."
              : "Mark this document as read so HR can track your acknowledgement."}
          </p>
          <div className="mt-4 flex gap-2 flex-wrap">
            {requiresSig ? (
              <Button onClick={sign} disabled={busy}>
                <PenLine className="h-4 w-4" />
                Sign document
              </Button>
            ) : (
              <Button onClick={acknowledge} disabled={busy}>
                <CheckCircle2 className="h-4 w-4" />I acknowledge
              </Button>
            )}
            <Button
              variant="secondary"
              onClick={() => navigate("/my-documents")}
              disabled={busy}
            >
              Back
            </Button>
          </div>
        </Card>
      )}

      {isDone && (
        <Card className="border-success/30 bg-success/5">
          <div className="flex items-center gap-2">
            <CheckCircle2 className="h-5 w-5 text-success" />
            <p className="text-sm text-text-primary font-medium">
              {assignment.status === "signed"
                ? "You've signed this document."
                : "This document has been waived."}
            </p>
          </div>
        </Card>
      )}
    </motion.div>
  );
}

/**
 * Inline form-fill experience for digital-form assignments.
 *
 * Owns its own state (schema + working data) so a submit/save flow
 * doesn't churn the parent's render cycle. Calls back into
 * ``onSubmitted`` after a successful submit so the parent re-fetches
 * the assignment row and flips the page into the read-only "Signed"
 * state.
 *
 * Loads via ``GET /document-assignments/my/documents/{id}/form`` —
 * that endpoint returns schema + draft data + already-submitted data
 * uniformly, so the same component handles fresh / partial / done.
 */
function FormFillCard({
  assignmentId,
  onSubmitted,
  isDone,
}: {
  assignmentId: string;
  onSubmitted: () => void;
  isDone: boolean;
}) {
  const { addToast } = useToast();
  const [schema, setSchema] = useState<FormField[]>([]);
  const [data, setData] = useState<Record<string, unknown>>({});
  const [loading, setLoading] = useState(true);
  const [savingDraft, setSavingDraft] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  // When the admin sent the form back, ``revision_note`` is set; the
  // form remains editable and we surface the note in a yellow banner
  // at the top so the employee sees the requested changes first.
  const [revisionNote, setRevisionNote] = useState<string | null>(null);
  // Track the form's actual status so we can re-derive editability —
  // ``revision_requested`` looks "done" via the parent's ``status ===
  // 'signed'`` check but is actually editable again.
  const [serverStatus, setServerStatus] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await getMyForm(assignmentId);
        if (cancelled) return;
        const sch = (res.form_schema as unknown as FormField[]) ?? [];
        setSchema(sch);
        // Existing values from server take precedence; missing fields
        // get type-appropriate defaults so the renderer never reads
        // ``undefined``.
        setData({ ...emptyFormData(sch), ...(res.form_data ?? {}) });
        setRevisionNote(res.revision_note ?? null);
        setServerStatus(res.status);
      } catch {
        addToast("error", "Couldn't load this form");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [assignmentId]);

  // ``isDone`` (parent) derives from the assignment row's status.
  // For revision_requested, the assignment is editable even though
  // a previous submission exists, so we override.
  const editable = serverStatus === "revision_requested" || !isDone;

  const update = (id: string, value: unknown) =>
    setData((d) => ({ ...d, [id]: value }));

  const saveDraft = async () => {
    setSavingDraft(true);
    try {
      await saveMyFormDraft(assignmentId, data);
      addToast("success", "Draft saved — you can come back later.");
    } catch (err: unknown) {
      const ax = err as { response?: { data?: { detail?: string } } };
      addToast("error", ax.response?.data?.detail || "Couldn't save draft");
    } finally {
      setSavingDraft(false);
    }
  };

  const submit = async () => {
    const v = validateFormData(schema, data);
    if (!v.ok) {
      addToast("error", v.firstError);
      return;
    }
    setSubmitting(true);
    try {
      await submitMyForm(assignmentId, data);
      addToast("success", "Submitted");
      onSubmitted();
    } catch (err: unknown) {
      const ax = err as { response?: { data?: { detail?: string } } };
      addToast("error", ax.response?.data?.detail || "Couldn't submit");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Card className="mb-4">
      <CardHeader>
        <CardTitle>Fill out this form</CardTitle>
        {!isDone && (
          <span className="text-xs text-text-muted">
            Save Draft to come back later · Submit when complete
          </span>
        )}
      </CardHeader>

      {loading ? (
        <div className="py-10 text-center text-text-muted">
          <Loader2 className="h-6 w-6 animate-spin mx-auto" />
        </div>
      ) : (
        <>
          {/* Admin-requested-revision banner. Renders any time the
              server says the assignment is in ``revision_requested``
              status, regardless of whether the parent thought it
              was done. */}
          {revisionNote && editable && (
            <div className="mb-4 rounded-md border border-warning/40 bg-warning/5 px-3 py-3">
              <p className="text-sm font-medium text-text-primary">
                Your admin requested changes
              </p>
              <p className="text-sm text-text-muted mt-1">{revisionNote}</p>
              <p className="text-xs text-text-muted mt-2">
                Update the fields below and submit again. You can save a
                draft any time.
              </p>
            </div>
          )}
          <FormRenderer
            schema={schema}
            data={data}
            onChange={update}
            disabled={!editable}
          />
          {editable && (
            <div className="mt-6 flex justify-end gap-2">
              <Button
                variant="secondary"
                onClick={saveDraft}
                isLoading={savingDraft}
                disabled={submitting}
              >
                Save Draft
              </Button>
              <Button onClick={submit} isLoading={submitting}>
                {serverStatus === "revision_requested"
                  ? "Resubmit"
                  : "Submit"}
              </Button>
            </div>
          )}
        </>
      )}
    </Card>
  );
}

export default MyDocumentDetailPage;
